import React, { useState } from "react";
import { Formik, Form, Field } from "formik";
import { Box, Typography, Divider } from "@material-ui/core";
import AppButton from "components/CustomButtons/AppButton";
import AppColor from "utilities/AppColor";
import { useDispatch, useSelector } from "react-redux";
import { submitIntakeLinkAgreementConsent, getIntakeLinkAgreementConsentItem } from "store/slices/client";
import { showToast } from "store/slices/toast";
import snakecaseKeys from "snakecase-keys";
import { useTranslation } from "react-i18next";

const CustomFileUploadBox = ({ label, file, onFileSelect, initialFileName }) => {
  const { t } = useTranslation();
  const fileInputRef = React.useRef(null);
  const displayFileName = file ? file.name : (initialFileName ? initialFileName : "No file selected");
  return (
    <Box display="flex" alignItems="center" p={1.5} bgcolor="#F9FAFB" border="1px solid #E5E7EB" borderRadius={6}>
      <i className="fas fa-file-pdf" style={{ fontSize: 18, color: "#EF4444", marginRight: 8 }} />
      <Box flex={1} textOverflow="ellipsis" whiteSpace="nowrap" overflow="hidden" fontSize={13} color="#374151">
        {displayFileName === "No file selected" ? t("text.no_file_selected") : displayFileName}
      </Box>
      <AppButton
        label={file || initialFileName ? t("button.change") : label}
        mode="outlined"
        rounder
        style={{ fontSize: 11, textTransform: "none", height: 28, marginLeft: 8 }}
        onClick={() => {
          if (fileInputRef.current) {
            fileInputRef.current.value = null;
            fileInputRef.current.click();
          }
        }}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/jpg,application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files[0];
          if (f) onFileSelect(f);
        }}
      />
    </Box>
  );
};

const IntakeLinkAgreementConsent = ({ clientData, token, onSuccess }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const companyConfig = useSelector((state) => state.auth.companyConfig);
  const [loading, setLoading] = useState(false);
  const [consentItem, setConsentItem] = useState(null);

  React.useEffect(() => {
    dispatch(getIntakeLinkAgreementConsentItem(token)).unwrap().then(res => {
      setConsentItem(res);
    }).catch(err => {
      console.log(err);
    });
  }, [dispatch, token]);

  const initialValues = {
    privacyAgreed: clientData?.privacyAgreed || false,
    informationSharingAgreed: clientData?.informationSharingAgreed || false,
    serviceAgreementFile: null,
  };

  const handleDownloadAgreementFileError = () => {
    dispatch(showToast({ message: "Service Agreement Template is not available", type: "error" }));
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    const payload = {
      intakeLinkToken: token,
      privacyAgreed: values.privacyAgreed,
      informationSharingAgreed: values.informationSharingAgreed,
    };

    const formData = new FormData();
    const snakeCasePayload = snakecaseKeys(payload);
    formData.append("content-data", new Blob([JSON.stringify(snakeCasePayload)], { type: "application/json" }));
    
    if (values.serviceAgreementFile) {
      formData.append("serviceAgreementFile", values.serviceAgreementFile);
    }
    
    try {
      const response = await dispatch(submitIntakeLinkAgreementConsent(formData));
      if (response && response.payload) {
        onSuccess();
      }
    } catch (e) {
      console.log(e);
      dispatch(showToast({ message: "Failed to submit Agreement & Consent", type: "error" }));
    }
    setLoading(false);
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      {({ isValid, setFieldValue, values }) => (
        <Form>
          <Typography variant="h6" style={{ color: AppColor.mainColor, marginBottom: 15 }}>
            {t("label.service_agreement_consent")}
          </Typography>
          <Divider style={{ marginBottom: 20 }} />

          <Box mt={3} mb={3}>
            <label style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
              <Field type="checkbox" name="privacyAgreed" style={{ marginRight: 10, width: 18, height: 18 }} />
              {t("text.agree_privacy_policy")}
            </label>
            <label style={{ display: "flex", alignItems: "center", marginBottom: 20 }}>
              <Field type="checkbox" name="informationSharingAgreed" style={{ marginRight: 10, width: 18, height: 18 }} />
              {t("text.consent_information_sharing")}
            </label>
            
            <Box mt={2}>
              <Typography variant="subtitle1" style={{ fontWeight: "bold", marginBottom: 5 }}>{t("label.service_agreement")}</Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 10 }}>
                {t("message.download_service_agreement")}
              </Typography>
              {
                companyConfig?.clientDownloadAgreementFile && companyConfig.clientDownloadAgreementFile !== ''
                ? <a href={companyConfig.clientDownloadAgreementFileAws3Link} download="Service_Agreement.pdf">
                  <Box style={{ color: AppColor.mainColor, fontWeight: "bold", display: "inline-block", marginBottom: 15, cursor: "pointer" }}>
                    <span>{t("button.download_service_agreement_template")}</span>
                    <i className="fas fa-download ml-2"></i>
                  </Box>
                </a>
                : <a onClick={handleDownloadAgreementFileError}>
                  <Box style={{ color: AppColor.mainColor, fontWeight: "bold", display: "inline-block", marginBottom: 15, cursor: "pointer" }}>
                    <span>{t("button.download_service_agreement_template")}</span>
                    <i className="fas fa-download ml-2"></i>
                  </Box>
                </a>
              }
              <div className="row">
                <div className="col-sm-12 col-md-12">
                  <CustomFileUploadBox
                    label={t("button.upload_signed_agreement", "Upload Signed Agreement")}
                    onFileSelect={(f) => setFieldValue("serviceAgreementFile", f)}
                    file={values.serviceAgreementFile}
                    initialFileName={consentItem?.fileName}
                  />
                </div>
              </div>
            </Box>
          </Box>

          <Box mt={4} display="flex" justifyContent="flex-end">
            <AppButton
              label={t("button.submit_form", "Submit Form")}
              type="submit"
              buttonColor={AppColor.mainColor}
              isLoading={loading}
              disabled={!isValid || loading || !values.informationSharingAgreed || !values.privacyAgreed}
              style={{ width: "200px" }}
            />
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export default IntakeLinkAgreementConsent;
